import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiService} from './api.service';
import {ConnectOAuthPopupService} from '../connect/connect-oauth-popup.service';
import {EnrichConsoleDiagnosticsService} from './enrich-console-diagnostics.service';

/**
 * Service for handling HubSpot authentication
 * This service manages the OAuth flow for HubSpot integration
 */
@Injectable({
  providedIn: 'root'
})
export class HubspotAuthService {
    private apiService = inject(ApiService);
    private oauthPopup = inject(ConnectOAuthPopupService);
    private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

    private readonly providerRef = 'hubspot.oauth';
    private readonly connectionRef = 'hubspot';
    private readonly scopeSet = 'enrich';

  /** Opens a browser-bound flow and completes only after the protected callback. */
  async initiateAuth(clientId: string, connectionRef?: string): Promise<boolean> {
    try {
      await this.oauthPopup.authorize({
        provider_ref: this.providerRef, client_id: clientId,
        connection_ref: (connectionRef || this.connectionRef).trim(), scope_set: this.scopeSet,
      });
      return true;
    } catch (error) {
      this.enrichDiagnostics.reportError('enrich.hubspot-auth.initiate', error);
      return false;
    }
  }

  /**
   * Checks if the client has a valid HubSpot authentication
   * @param clientId The client ID to check
   * @returns An Observable with the authentication status
   */
  checkAuthStatus(clientId: string): Observable<{ authenticated: boolean, error?: string }> {
    return this.apiService.get<{ authenticated: boolean, error?: string }>('hubspot/auth-check', { clientId });
  }

    checkAuthStatusForConnection(
        clientId: string,
        connectionId: string | null | undefined,
    ): Observable<{ authenticated: boolean, error?: string }> {
        const conn = (connectionId || '').trim();
        return this.apiService.get<{ authenticated: boolean, error?: string }>('hubspot/auth-check', {
            clientId,
            ...(conn ? {connectionId: conn} : {}),
        });
    }

  /**
   * Manually triggers a refresh of the HubSpot access token
   * @param clientId The client ID to refresh the token for
   * @returns An Observable with the refresh result
   */
  refreshToken(clientId: string): Observable<{ message: string }> {
      return this.apiService.post<{ message: string }>('hubspot/refresh-token', {}, {clientId});
  }
}
